import React, { useState, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getMsteams, getMsteamChannels, setMsteamChannel } from '../../services';
import { HttpStatus } from '../../constants/httpStatus';
import { LoadingButton } from '../LoadingButton';
import { setIntegrationAvailable, setIntegrationData, setIntegrationActiveInstance } from '../../redux/slices/integrationsSlice';
import { selectAuthToken } from '../../redux/selectors/authSelectors';
import {
  StyledModal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  SectionTitle,
  SectionDescription,
  ChannelList,
  ChannelItem,
  ChannelName,
  ChannelDescription,
  TeamsList,
  TeamItem,
  EmptyState,
  CancelButton,
  SaveButton
} from './IntegrationModal.styles';

const TeamsConfigModal = ({ isOpen, onClose, onSave }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);

  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [channels, setChannels] = useState([]);
  const [checkedChannels, setCheckedChannels] = useState({});

  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      setIsLoadingTeams(true);
      setTeams([]);
      setChannels([]);
      setSelectedTeam(null);
      setCheckedChannels({});

      getMsteams(token)
        .then((res) => {
          if (cancelled) return;
          if (res && res.status === HttpStatus.OK && res?.teams?.length) {
            setTeams(res.teams);
            dispatch(
              setIntegrationData({
                id: 'msteams',
                data: { instances: res.teams },
              })
            );

            const activeTeam = res.teams.find(
              ({ applicationTeam }) => applicationTeam === true
            );
            if (activeTeam) {
              setSelectedTeam(activeTeam.teamId);
            }
          }
        })
        .catch(() => {
          if (!cancelled) {
            toast.error('Failed to load Teams instances. Please try again.', {
              position: 'top-right',
              autoClose: 4000,
            });
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoadingTeams(false);
        });

      return () => { cancelled = true; };
    }
  }, [isOpen, token, dispatch]);

  useEffect(() => {
    if (selectedTeam) {
      let cancelled = false;
      setIsLoadingChannels(true);
      setChannels([]);
      setCheckedChannels({});

      getMsteamChannels(selectedTeam)
        .then((res) => {
          if (cancelled) return;
          if (res && res.status === 200 && res?.channels?.length) {
            setChannels(res.channels);
            const defaultChannels = {};
            res.channels.forEach(({ channelId, applicationChannel }) => {
              if (applicationChannel === true) {
                defaultChannels[channelId] = true;
              }
            });
            setCheckedChannels(defaultChannels);
          }
        })
        .catch(() => {
          if (!cancelled) {
            toast.error('Failed to load Teams channels. Please try again.', {
              position: 'top-right',
              autoClose: 4000,
            });
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoadingChannels(false);
        });

      return () => { cancelled = true; };
    }
  }, [selectedTeam]);

  const handleTeamSelect = (teamId) => {
    setSelectedTeam(teamId);
    dispatch(
      setIntegrationActiveInstance({
        id: 'msteams',
        instanceId: teamId,
      })
    );
  };

  const handleChannelChange = (event) => {
    setCheckedChannels({
      ...checkedChannels,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSave = async () => {
    if (!selectedTeam) {
      toast.error('Please select a Teams instance first.', {
        position: 'top-right',
        autoClose: 4000,
      });
      return;
    }

    setIsSaving(true);
    const selectedChannelIds = Object.keys(checkedChannels).filter(
      (key) => checkedChannels[key] === true
    );

    try {
      const status = await setMsteamChannel(selectedChannelIds, selectedTeam);
      if (status === true) {
        toast.success('Success: Set channel(s) as indicated', {
          position: 'top-right',
          autoClose: 4000,
        });
        dispatch(setIntegrationAvailable({ id: 'msteams', available: true }));
        const selectedChannelsFull = channels.filter(
          (channel) => selectedChannelIds.indexOf(channel.channelId) > -1
        );
        dispatch(
          setIntegrationData({
            id: 'msteams',
            data: { channels: selectedChannelsFull },
          })
        );
        onSave?.();
      } else {
        toast.error('An error occurred saving your channel selection.', {
          position: 'top-right',
          autoClose: 4000,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = isLoadingTeams;

  return (
    <StyledModal
      show={isOpen}
      onHide={onClose}
      centered
      size="lg"
    >
        <ModalHeader closeButton>
        <ModalTitle>Configure Microsoft Teams</ModalTitle>
      </ModalHeader>

      <ModalBody>
        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" size="sm" role="status" style={{ color: '#a3a5a9' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : teams.length > 0 ? (
          <>
            <SectionTitle>Select instance</SectionTitle>
            <TeamsList>
              {teams.map(({ teamId, name }) => (
                <TeamItem
                  key={teamId}
                  $active={selectedTeam === teamId}
                  onClick={() => handleTeamSelect(teamId)}
                >
                  {name}
                </TeamItem>
              ))}
            </TeamsList>

            {selectedTeam && (
              <>
                <SectionTitle>Select channels</SectionTitle>
                <SectionDescription>
                  Choose which channels will receive shared documents and messages.
                </SectionDescription>
                {isLoadingChannels ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" size="sm" role="status" style={{ color: '#a3a5a9' }}>
                      <span className="visually-hidden">Loading channels...</span>
                    </Spinner>
                  </div>
                ) : channels.length > 0 ? (
                  <ChannelList>
                    {channels.map(({ channelId, name, topic, purpose }) => (
                      <ChannelItem key={channelId}>
                        <Form.Check type="checkbox" id={channelId} className="w-100">
                          <Form.Check.Input
                            type="checkbox"
                            name={channelId}
                            checked={checkedChannels[channelId] || false}
                            onChange={handleChannelChange}
                          />
                          <Form.Check.Label>
                            <ChannelName>{name}</ChannelName>
                            {(topic || purpose) && (
                              <ChannelDescription>
                                {topic}
                                {purpose && purpose !== '' && ` (${purpose})`}
                              </ChannelDescription>
                            )}
                          </Form.Check.Label>
                        </Form.Check>
                      </ChannelItem>
                    ))}
                  </ChannelList>
                ) : (
                  <EmptyState>
                    <p>No channels found for this instance.</p>
                  </EmptyState>
                )}
              </>
            )}
          </>
        ) : (
          <EmptyState>
            <h5>No instances found</h5>
            <p>
              Microsoft Teams isn't connected or no instances are available. Check your connection and try again.
            </p>
          </EmptyState>
        )}
      </ModalBody>

      <ModalFooter>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <LoadingButton
          as={SaveButton}
          onClick={handleSave}
          loading={isSaving}
          loadingText="Saving..."
          disabled={isLoading || teams.length === 0 || !selectedTeam}
        >
          Save
        </LoadingButton>
      </ModalFooter>
    </StyledModal>
  );
};

export default TeamsConfigModal;
